import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../app/store/store';
import {
  fetchSchedules,
  updateSchedule,
  createSchedule,
} from '../../../entities/helpdesk-settings/model';
import { helpdeskSettingsApi } from '../../../api/services/helpdesk-settings.service';
import type { OperatorScheduleEntry, OperatorBrief } from '../../../types/helpdeskSettingsInterfaces';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

/* ── Хелпер: группировка по оператору ── */
interface OperatorGroup {
  operatorId: number;
  operatorName: string;
  initials: string;
  timezone: string;
  summary: string;
  entries: OperatorScheduleEntry[];
}

const groupByOperator = (schedules: OperatorScheduleEntry[]): OperatorGroup[] => {
  const map = new Map<number, OperatorScheduleEntry[]>();
  schedules.forEach((s) => {
    const arr = map.get(s.operator) || [];
    arr.push(s);
    map.set(s.operator, arr);
  });

  return Array.from(map.entries()).map(([opId, entries]) => {
    const sorted = [...entries].sort((a, b) => a.weekday - b.weekday);
    const tz = sorted[0]?.timezone || 'UTC';
    const name = sorted[0]?.operator_name || `Оператор #${opId}`;

    // Build summary like "Пн-Пт · 09:00-18:00"
    const workDays = sorted.filter((e) => e.is_active).map((e) => e.weekday);
    let summary = '';
    if (workDays.length === 0) {
      summary = 'Нет расписания';
    } else if (workDays.length === 5 && workDays[0] === 0 && workDays[4] === 4) {
      summary = `Пн-Пт · ${sorted[0].start_time?.slice(0, 5)}-${sorted[0].end_time?.slice(0, 5)}`;
    } else {
      summary = workDays.map((d) => WEEKDAYS[d]).join(', ');
      if (sorted[0]) summary += ` · ${sorted[0].start_time?.slice(0, 5)}-${sorted[0].end_time?.slice(0, 5)}`;
    }

    // Initials from name
    const parts = name.split(' ');
    const initials = parts.length >= 2
      ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
      : name.slice(0, 2).toUpperCase();

    return {
      operatorId: opId,
      operatorName: name,
      initials,
      timezone: tz,
      summary,
      entries: sorted,
    };
  });
};

/* ── Inline-редактор дня ── */
interface DayEditorProps {
  weekday: number;
  entry?: OperatorScheduleEntry;
  onSave: (weekday: number, startTime: string, endTime: string, isActive: boolean) => void;
}

const DayEditor: React.FC<DayEditorProps> = ({ weekday, entry, onSave }) => {
  const isOff = !entry || !entry.is_active;
  const [startTime, setStartTime] = useState(entry?.start_time?.slice(0, 5) || '09:00');
  const [endTime, setEndTime] = useState(entry?.end_time?.slice(0, 5) || '18:00');

  return (
    <div
      className={`flex flex-col items-center justify-center px-3 py-3 rounded-lg border text-center min-w-[100px] ${
        isOff
          ? 'border-gray-200 bg-gray-50 text-gray-400'
          : 'border-[#e3282a]/20 bg-red-50/30 text-[#2d2c2c]'
      }`}
    >
      <span className={`text-sm font-semibold mb-1 ${isOff ? 'text-gray-400' : 'text-[#e3282a]'}`}>
        {WEEKDAYS[weekday]}
      </span>
      {isOff ? (
        <button
          onClick={() => onSave(weekday, '09:00', '18:00', true)}
          className="text-xs text-gray-400 hover:text-[#e3282a] transition-colors"
        >
          выходной
        </button>
      ) : (
        <div className="flex flex-col items-center gap-1">
          <input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            onBlur={() => onSave(weekday, startTime, endTime, true)}
            className="w-20 text-xs text-center border-0 bg-transparent focus:outline-none"
          />
          <span className="text-xs text-gray-300">—</span>
          <input
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            onBlur={() => onSave(weekday, startTime, endTime, true)}
            className="w-20 text-xs text-center border-0 bg-transparent focus:outline-none"
          />
          <button
            onClick={() => onSave(weekday, startTime, endTime, false)}
            className="text-[10px] text-gray-400 hover:text-red-500 mt-1"
          >
            выходной
          </button>
        </div>
      )}
    </div>
  );
};

/* ── Карточка оператора ── */
interface OperatorCardProps {
  group: OperatorGroup;
  expanded: boolean;
  onToggleExpand: () => void;
  onUpdateDay: (operatorId: number, weekday: number, startTime: string, endTime: string, isActive: boolean, existingEntryId?: number) => void;
}

const OperatorCard: React.FC<OperatorCardProps> = ({ group, expanded, onToggleExpand, onUpdateDay }) => {
  const entryMap = new Map(group.entries.map((e) => [e.weekday, e]));

  const bgColors = ['bg-red-100 text-red-600', 'bg-blue-100 text-blue-600', 'bg-green-100 text-green-600', 'bg-purple-100 text-purple-600'];
  const color = bgColors[group.operatorId % bgColors.length];

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div className="flex items-center justify-between py-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${color}`}>
            {group.initials.slice(0, 2)}
          </div>
          <div>
            <p className="text-sm font-medium text-[#2d2c2c]">{group.operatorName}</p>
            <p className="text-xs text-gray-400">{group.summary}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-gray-400">{group.timezone}</span>
          <button
            onClick={onToggleExpand}
            className="text-sm font-medium text-[#e3282a] hover:text-[#c82022] transition-colors"
          >
            {expanded ? 'Свернуть' : 'Изменить'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="pb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">Расписание</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {[0, 1, 2, 3, 4, 5, 6].map((day) => (
              <DayEditor
                key={day}
                weekday={day}
                entry={entryMap.get(day)}
                onSave={(wd, st, et, active) => {
                  const existing = entryMap.get(wd);
                  onUpdateDay(group.operatorId, wd, st, et, active, existing?.id);
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Модалка добавления оператора ── */
interface AddOperatorModalProps {
  operators: OperatorBrief[];
  existingOperatorIds: Set<number>;
  onClose: () => void;
  onAdd: (operatorId: number, timezone: string) => void;
}

const AddOperatorModal: React.FC<AddOperatorModalProps> = ({ operators, existingOperatorIds, onClose, onAdd }) => {
  const available = operators.filter((o) => !existingOperatorIds.has(o.id));
  const [selectedId, setSelectedId] = useState<number | null>(available[0]?.id ?? null);
  const [timezone, setTimezone] = useState('Europe/Moscow');

  const TIMEZONES = [
    'Europe/Kaliningrad',
    'Europe/Moscow',
    'Europe/Samara',
    'Asia/Yekaterinburg',
    'Asia/Omsk',
    'Asia/Krasnoyarsk',
    'Asia/Irkutsk',
    'Asia/Yakutsk',
    'Asia/Vladivostok',
    'Asia/Magadan',
    'Asia/Kamchatka',
  ];

  const handleSubmit = () => {
    if (selectedId) onAdd(selectedId, timezone);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">Добавить оператора</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {available.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              Все операторы уже добавлены, либо нет пользователей с HD-ролями.
              Назначьте роль в разделе «Пользователи и роли».
            </p>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Оператор</label>
                <select
                  value={selectedId ?? ''}
                  onChange={(e) => setSelectedId(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
                >
                  {available.map((op) => (
                    <option key={op.id} value={op.id}>
                      {op.full_name} ({op.username})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Часовой пояс</label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Отмена
          </button>
          {available.length > 0 && (
            <button
              onClick={handleSubmit}
              disabled={!selectedId}
              className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] transition-colors disabled:opacity-50"
            >
              Добавить
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Основной экран ── */
const ScheduleSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { schedules, schedulesStatus } = useSelector(
    (state: RootState) => state.helpdeskSettings,
  );
  const [expandedOperator, setExpandedOperator] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableOperators, setAvailableOperators] = useState<OperatorBrief[]>([]);
  const [loadingOperators, setLoadingOperators] = useState(false);

  useEffect(() => {
    if (schedulesStatus === 'idle') dispatch(fetchSchedules());
  }, [dispatch, schedulesStatus]);

  const loadOperators = useCallback(async () => {
    setLoadingOperators(true);
    try {
      const res = await helpdeskSettingsApi.getAvailableOperators();
      setAvailableOperators(res.data);
    } catch {
      setAvailableOperators([]);
    }
    setLoadingOperators(false);
  }, []);

  const handleOpenAddModal = () => {
    loadOperators();
    setShowAddModal(true);
  };

  const operatorGroups = groupByOperator(schedules);
  const existingOperatorIds = new Set(operatorGroups.map((g) => g.operatorId));

  const handleUpdateDay = useCallback(
    (operatorId: number, weekday: number, startTime: string, endTime: string, isActive: boolean, existingEntryId?: number) => {
      if (existingEntryId) {
        dispatch(updateSchedule({
          id: existingEntryId,
          data: { start_time: startTime, end_time: endTime, is_active: isActive },
        }));
      } else {
        dispatch(createSchedule({
          operator: operatorId,
          weekday,
          start_time: startTime,
          end_time: endTime,
          timezone: 'Europe/Moscow',
          is_active: isActive,
        }));
      }
    },
    [dispatch],
  );

  const handleAddOperator = useCallback(
    (operatorId: number, timezone: string) => {
      // Create default Mon-Fri 09:00-18:00 schedule
      for (let day = 0; day <= 4; day++) {
        dispatch(createSchedule({
          operator: operatorId,
          weekday: day,
          start_time: '09:00',
          end_time: '18:00',
          timezone,
          is_active: true,
        }));
      }
      // Sat-Sun as inactive
      for (let day = 5; day <= 6; day++) {
        dispatch(createSchedule({
          operator: operatorId,
          weekday: day,
          start_time: '09:00',
          end_time: '18:00',
          timezone,
          is_active: false,
        }));
      }
      setShowAddModal(false);
    },
    [dispatch],
  );

  if (schedulesStatus === 'loading' && schedules.length === 0) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-1/3"></div>
          <div className="h-16 bg-gray-100 rounded"></div>
          <div className="h-16 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      {/* Заголовок «Расписание операторов» переехал в шапку — внутри
          карточки оставляем только action-кнопку, прижатую вправо. */}
      <div className="flex items-center justify-end mb-4">
        <button
          onClick={handleOpenAddModal}
          className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] transition-colors"
        >
          + Добавить оператора
        </button>
      </div>

      {operatorGroups.length > 0 ? (
        <div>
          {operatorGroups.map((group) => (
            <OperatorCard
              key={group.operatorId}
              group={group}
              expanded={expandedOperator === group.operatorId}
              onToggleExpand={() =>
                setExpandedOperator(expandedOperator === group.operatorId ? null : group.operatorId)
              }
              onUpdateDay={handleUpdateDay}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400 text-center py-8">
          Нет расписаний. Нажмите «Добавить оператора», чтобы создать расписание.
        </p>
      )}

      {showAddModal && !loadingOperators && (
        <AddOperatorModal
          operators={availableOperators}
          existingOperatorIds={existingOperatorIds}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddOperator}
        />
      )}
    </div>
  );
};

export default ScheduleSettings;
